import { IconType } from 'react-icons';
import { LuArchive, LuKeyRound, LuMapPin } from 'react-icons/lu';
import { House } from '@/client';
import { isOwnedArchiveCopy, isOwnedProperty } from '@/utils/properties/propertyGroupingUtils';

export const PropertySelectorGroupKey = {
	Owned: 'owned',
	Shared: 'shared',
	Archive: 'archive',
} as const;
export type PropertySelectorGroupKey = (typeof PropertySelectorGroupKey)[keyof typeof PropertySelectorGroupKey];

export const PropertySelectorLabel = {
	Owned: 'My Property Bundles',
	Shared: 'Shared with Me',
	Archive: 'My Archive Copies',
} as const;

export const PROPERTY_SELECTOR_LOCKED_ROW_OPACITY = 0.56;

/**
 * Desktop selector inset system. One column for the search glyph, every property-row icon,
 * and the add "+": icon column = SURFACE_INSET + ROW_INSET.
 * SURFACE_INSET insets the interactive surfaces (row/add highlights, section dividers) from
 * the menu edge; ROW_INSET insets the icon/content inside each surface.
 */
export const PROPERTY_SELECTOR_DESKTOP_SURFACE_INSET = 2;
/**
 * Inner pad from the surface to the icon. Tuned so row/header icons share the search
 * magnifier's column (the seamless search's InputGroup adds its own start offset).
 */
export const PROPERTY_SELECTOR_DESKTOP_ROW_INSET = 3;
/**
 * The add row's "+" is a larger icon (boxSize 6) than the row pins, so it needs a slightly
 * smaller inset than ROW_INSET to optically land its left edge on the same column.
 */
export const PROPERTY_SELECTOR_DESKTOP_ADD_ROW_INSET = 2;
/**
 * Extra inset from the menu edge, shared by the search row, the scrolling list and the add
 * row so all three stay equal width. The scrollbar floats over this strip rather than taking
 * layout space, so there is nothing to compensate for and one value serves all three.
 */
export const PROPERTY_SELECTOR_DESKTOP_EDGE_INSET = 1;

/**
 * Leading icon for a property row, resolved from the property's own classification rather than
 * its group. Only the user's own retained clone reads as archive; a clone shared with the user
 * by someone else shows the shared key. Archive is checked before owned since an owned clone
 * also reports as owned.
 */
export const getPropertySelectorRowIcon = (property: House, userId?: number): IconType => {
	if (isOwnedArchiveCopy(property, userId)) return LuArchive;
	if (isOwnedProperty(property, userId)) return LuMapPin;
	return LuKeyRound;
};
